import type { PermissionModule } from "@/services/userApi";
import type { CycleScope } from "@/types";
import { plural } from "@/utils/format";

interface PermissionMatrixProps {
  modules: PermissionModule[];
  selected: string[];
  onChange: (next: string[]) => void;
  /** Rôle limité à un cycle : les permissions « globales » ne sont pas disponibles. */
  cycleScope: CycleScope;
}

export default function PermissionMatrix({ modules, selected, onChange, cycleScope }: PermissionMatrixProps) {
  const active = new Set(selected);

  const toggleOne = (id: string) => {
    const next = new Set(active);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange([...next]);
  };

  const toggleModule = (ids: string[], on: boolean) => {
    const next = new Set(active);
    ids.forEach((id) => (on ? next.add(id) : next.delete(id)));
    onChange([...next]);
  };

  return (
    <div className="space-y-3">
      {modules.map((mod, index) => {
        const groupStart = index === 0 || modules[index - 1].group !== mod.group;
        const isAvailable = (p: { globalOnly?: boolean }) => cycleScope === "GLOBAL" || !p.globalOnly;
        const ids = mod.permissions.filter(isAvailable).map((p) => p.id);
        const checkedCount = ids.filter((id) => active.has(id)).length;
        const allOn = checkedCount === ids.length && ids.length > 0;
        const someOn = checkedCount > 0 && !allOn;

        return (
          <div key={mod.module}>
            {groupStart && (
              <p className="mb-2 mt-4 text-xs font-semibold uppercase tracking-wide text-foreground-400 first:mt-0">
                {mod.group}
              </p>
            )}
          <div className="rounded-lg border border-background-200 bg-background-50">
            <div className="flex items-center justify-between gap-3 border-b border-background-200 px-4 py-2.5">
              <div className="flex items-center gap-2.5">
                <span className="w-7 h-7 rounded-md bg-secondary-100 text-secondary-700 flex items-center justify-center shrink-0">
                  <i className={`${mod.icon} text-base`}></i>
                </span>
                <div>
                  <p className="text-sm font-medium text-foreground-900">{mod.label}</p>
                  <p className="text-xs text-foreground-500">
                    {checkedCount}/{plural(ids.length, "permission")}
                  </p>
                </div>
              </div>
              <label className="flex items-center gap-2 whitespace-nowrap text-xs text-foreground-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={allOn}
                  ref={(el) => {
                    if (el) el.indeterminate = someOn;
                  }}
                  onChange={(e) => toggleModule(ids, e.target.checked)}
                  className="h-3.5 w-3.5 accent-primary-500 cursor-pointer"
                />
                Tout cocher
              </label>
            </div>
            <div className="grid grid-cols-1 gap-2 p-3 sm:grid-cols-2">
              {mod.permissions.map((perm) => {
                const on = active.has(perm.id);
                const available = isAvailable(perm);
                return (
                  <label
                    key={perm.id}
                    title={available ? undefined : "Réservée aux rôles « Global » (les deux cycles)"}
                    className={`flex items-center gap-2 rounded-md border px-3 py-2 text-xs transition-colors ${
                      !available
                        ? "border-background-200 bg-background-100 text-foreground-400 cursor-not-allowed"
                        : on
                          ? "border-primary-300 bg-primary-50 text-primary-900 cursor-pointer"
                          : "border-background-200 bg-background-50 text-foreground-600 hover:border-background-300 cursor-pointer"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={on && available}
                      disabled={!available}
                      onChange={() => toggleOne(perm.id)}
                      className="h-3.5 w-3.5 accent-primary-500 cursor-pointer disabled:cursor-not-allowed"
                    />
                    {perm.label}
                    {!available && <i className="ri-global-line ml-auto"></i>}
                  </label>
                );
              })}
            </div>
          </div>
          </div>
        );
      })}
    </div>
  );
}